import React, { useState, useEffect } from 'react';
import { Link, useLocation  } from 'react-router-dom';
import { useCompanyInfo } from '../../context/CompanyInfoContext';
import {
  HomeIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UsersIcon,
  BriefcaseIcon,
  CalculatorIcon,
  FolderIcon,
  TagIcon,
  WrenchScrewdriverIcon,
  ShoppingCartIcon,
  ListBulletIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  TruckIcon,
  ChartBarIcon,
  UserGroupIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { CalendarIcon, ClockIcon } from 'lucide-react';  
import { useRoutePath } from '../../utils/getRoutePath';
import { usePermissions } from '../../context/PermissionsContext';

const Sidebar = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const { companyInfo } = useCompanyInfo()
  const firstSegment = window.location.pathname.split('/').filter(Boolean)[0];
  const capitalize = (str) => str?.charAt(0)?.toUpperCase() + str?.slice(1) || '';
  const getRoutePath = useRoutePath();
  const { permission } = usePermissions();
   
  useEffect(() => {
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
  
    window.addEventListener('resize', handleResize);
  
    return () => {
     
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobile, isOpen]);

  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
       [menuName]: !prev[menuName]
     }));
   
  };
   
  let userMenuItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: HomeIcon,
      module_id:1,
    },
    {
      name: 'Calendar',
      href: '/calendar',
      icon: CalendarIcon,
      module_id:33,
    },
    {
      name: 'Customers',
      href: '/customers',
      icon: UsersIcon,
      module_id:28,
      submenu: [
        { name: 'Customer List', href: '/customers' ,module_id:28},
        { name: 'Add Customer', href: '/customers/new' ,module_id:28},
         
      ],
    },
    {
      name: 'Tasks',
      href: '/tasks',
      icon: ClipboardDocumentListIcon,
      submenu: [
        { name: 'Task List', href: '/tasks' },
        { name: 'Add Task', href: '/tasks/new' },
         
      ],
    },
    {
      name: 'Time Off Requests',
      href: '/time-off-requests',
      icon: ClockIcon,
      submenu: [
        { name: 'Time Off Request List', href: '/time-off-requests' },
        { name: 'Add Time Off Request', href: '/time-off-requests/create' },
         
      ],
    },
    {
      name: 'Jobs',
      href: '/jobs',
      icon: BriefcaseIcon,
      module_id:39,
      submenu: [
        { name: 'Job List', href: '/jobs', module_id:39 },
        { name: 'Add Job', href: '/jobs/new', module_id:39},
      ],
    },
    {
      name: 'Projects',
      href: '/projects',
      icon: FolderIcon,
      module_id:29,
      submenu: [
        { name: 'Project List', href: '/projects' ,module_id:29},
        { name: 'Add Project', href: '/projects/create' ,module_id:29},
      ],
    },
    {
      name: 'Estimation Templates',
      href: '/estimation-templates',
      icon: CalculatorIcon,
      submenu: [
        { name: 'Estimation Template List', href: '/estimation-templates' },
        { name: 'Add Estimation Template', href: '/estimation-templates/new' },
      ],
    },
    {
      name: 'Estimations',
      href: '/estimations',
      icon: CalculatorIcon,
      module_id:40,
      submenu: [
        { name: 'Estimation List', href: '/estimations', module_id:40, },
        { name: 'Add Estimation', href: '/estimations/new', module_id:40,},
      ],
    },
    {
      name: 'Service Categories',
      href: '/service-categories',
      icon: TagIcon,
      module_id:23,
      submenu: [
        { name: 'Service Categories List', href: '/service-categories',module_id:23 },
        { name: 'Add Service Categories', href: '/service-categories/create',module_id:23 },
      ],
    },
    {
      name: 'Service',
      href: '/services',
      icon: WrenchScrewdriverIcon,
      module_id:23,
      submenu: [
        { name: 'Service List', href: '/services',module_id:23, },
        { name: 'Add Service', href: '/services/new',module_id:23, },
      ],
    },
    {
      name: 'Product Categories',
      href: '/product-categories',
      icon: TagIcon,
      module_id:21,
      submenu: [
        { name: 'Product Categories List', href: '/product-categories',module_id:21 },
        { name: 'Add Product Categories', href: '/product-categories/create',module_id:21 },
      ],
    },
    {
      name: 'Products',
      href: '/products',
      icon: ShoppingCartIcon,
      module_id:21,
      submenu: [
        { name: 'Product List', href: '/products',module_id:21 },
        { name: 'Add Product', href: '/products/create',module_id:21 },
      ],
    },
    {
      name: 'Vendors',
      href: '/vendors',
      icon: TruckIcon,
      module_id:19,
      submenu: [
        { name: 'Vendor List', href: '/vendors',module_id:19 },
        { name: 'Add Vendor', href: '/vendors/create',module_id:19 },
      ],
    },
    {
      name:'Companies',
      href:'/company-info',
      icon: BuildingOfficeIcon,
      module_id:4,
      submenu:[
        {name:'Company List',href:'/company-info',module_id:4},
        {name:'Add Company',href:'/company-info/create',module_id:4},
      ]
    },
    {
      name:'Company Workforces',
      href:'/company-workforces',
      icon: UsersIcon,
      module_id:14,
      submenu:[
        {name:'Company Workforce List',href:'/company-workforces',module_id:14},
        {name:'Add Company Workforce',href:'/company-workforces/create',module_id:14},
      ]
    },
    {
      name: 'List Management',
      href: '/list-management',
      icon: ListBulletIcon,
      submenu: [
        { name: 'Expense Categories', href: '/expense-categories' },
        { name: 'Job Categories', href: '/job-categories' },
        { name: 'Time Off Request Types', href: '/time-off-request-types' },
        { name: 'Payment Methods', href: '/payment-methods' },
        { name: 'Payment Terms', href: '/payment-terms' },
        { name: 'Equipment Manufacturers', href: '/equipment-manufacturers' },
        { name: 'Equipment Types', href: '/equipment-types' },
      ],
    },
    {
      name: 'Tag Management',
      href: '/tag-management',
      icon: TagIcon,
      submenu: [
        { name: 'Customer Tags', href: '/customer-tags' },
        { name: 'Estimate & Job Tags', href: '/estimate-job-tags' },
         
      ],
    }, 

    {
      name: 'Statuses',
      href: '/',
      icon: CheckCircleIcon,
      module_id:7,
      submenu: [
        {name:'Estimation Statuses',href:'/estimation-statuses',module_id:7},
        {name:'Job Statuses',href:'/job-statuses',module_id:7},
         
      ],
    },


    {
      name: 'Invoices',
      href: '/',
      icon: EnvelopeIcon,
      module_id:34,
      submenu: [
        {name:'Invoice List',href:'/invoices',module_id:34},
        {name:'Add Invoice',href:'/invoices/create',module_id:34},
         
      ],
    },

    {
      name: 'Payments',
      href: '/',
      icon: CreditCardIcon,
      module_id:35,
      submenu: [
        {name:'Payment List',href:'/payments',module_id:35},
        {name:'Add Payment',href:'/payments/create',module_id:35},
         
      ],
    },
    
    
    {
      name: 'Settings',
      href: '/settings',
      icon: Cog6ToothIcon,
      submenu: [
       
        { name: 'Communication  Templates', href: '/communication-templates' },
        
      ],
    },
    {
      name: 'Reports',
      href: '/reports',
      icon: ChartBarIcon,
      submenu: [
       
        { name: 'Report Dashboard', href: '/reports' },
      ],
    },
  ]; 

  let filteredMenuItems = userMenuItems
  .filter(item => {

    // Always show menu items with no module_id
    if (!item.module_id) return true;
     
    // Show only if read permission exists
    return permission(item.module_id, 'view');
  })
  .map(item => ({
    ...item,
    subItems: item.subItems
      ? item.subItems.filter(sub => {
          if (!sub.module_id) return true;
          return permission(sub.module_id, 'view');
        })
      : null,
  }));

  if ((user.role_id === 3)) {
    filteredMenuItems = filteredMenuItems.map(item => {
        if (item.href === '/company-info' && item.submenu) {
            item.submenu = item.submenu.filter(sub => sub.href !== '/company-info/create');
        }
        return item;
    });
  }

  if ((user.role_id === 2) && companyInfo?.data?.length >= 1) {
    filteredMenuItems = filteredMenuItems.map(item => {
        if (item.href === '/company-info' && item.submenu) {
            item.submenu = item.submenu.filter(sub => sub.href !== '/company-info/create');
        }
        return item;
    });
  }

  if (user.role_id !== 1 && user.role_id !== 2) {
    filteredMenuItems = filteredMenuItems.map(item => {
      if (item.href === '/company-workforces' && item.submenu) {
        item.submenu = item.submenu.filter(sub => sub.href !== '/company-workforces/create');
      }
      return item;
    });
  }
   
  const adminMenuItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: HomeIcon,
      
    },
    {
      name:'Roles',
      href:'/roles',
      icon: UserGroupIcon,
      submenu:[
        {name:'Role List',href:'/roles'},
        {name:'Add Role',href:'/roles/create'},
      ]
    },
    {
      name:'Companies',
      href:'/company-info',
      icon: BuildingOfficeIcon,
      submenu:[
        {name:'Company List',href:'/company-info'},
        {name:'Add Company',href:'/company-info/create'},
      ]
    },
    {
      name:'Company Workforces',
      href:'/company-workforces',
      icon: ListBulletIcon,
      submenu:[
        {name:'Company Workforce List',href:'/company-workforces'},
        {name:'Add Company Workforce',href:'/company-workforces/create'},
      ]
    },
     
    
  ];

  const menuItems = (user && typeof user === 'object' && (user.role_id === 1 || user.role_id === '1')) ? adminMenuItems : filteredMenuItems;

  function findMenuNameByFirstSegment(menuItems, firstSegment) {
    for (const item of menuItems) {
      if (item.submenu) {
        for (const subItem of item.submenu) {
          // Check if subItem.href includes or matches firstSegment
          if (subItem.href.includes(firstSegment)) {
            return item.name;  // Return the parent menu name
          }
        }
      }
      // Also check if top-level href matches firstSegment if needed:
      if (item.href && item.href.includes(firstSegment)) {
        return item.name;
      }
    }
    return null;  // Not found
  }

  useEffect(() => {
    const onLoad = () => {
      const timer = setTimeout(() => {
         const menuName = findMenuNameByFirstSegment(menuItems, firstSegment)
        toggleMenu(capitalize(menuName));
      }, 0); // 1 second delay
  
      // Cleanup timeout if the component unmounts before timeout fires
      return () => clearTimeout(timer);
    };
  
    window.addEventListener('load', onLoad);
  
    // Cleanup event listener and timeout on unmount
    return () => {
      window.removeEventListener('load', onLoad);
    };
  }, [firstSegment,menuItems]);

  // Recursive renderer for menu & submenu
  const renderMenuItem = (item, level = 0) => {
    const currentPath = location.pathname;

    // Recursive function to check if item or any child is active
    const isItemActive = (menuItem) => {
      if (menuItem.href && currentPath === getRoutePath(menuItem.href)) {
        return true;
      }
      if (menuItem.submenu) {
        return menuItem.submenu.some((sub) => isItemActive(sub));
      }
      return false;
    };

    const isActive = isItemActive(item);
      
    if (item.submenu) {
      return (
        <div key={item.name} className={`${level > 0 ? 'ml-4' : ''}`}>
         <button
          onClick={() => toggleMenu(item.name)}
          className={`flex items-center w-full py-2.5 text-sm rounded-lg transition-colors duration-200
            ${level === 0 ? 'px-3 font-medium' : 'px-0'}
            ${level === 0 && isActive ? 'bg-[#009efb] text-white shadow-md' : 'text-gray-700 hover:bg-gray-100'}
          `}
        >
            {item.icon && (
              <item.icon
                className={`mr-3 h-5 w-5 ${
                  level === 0 && isActive
                    ? 'text-white'
                    : 'text-gray-500 group-hover:text-gray-900'
                }`}
              />
            )}
            {item.name}
  
            {expandedMenus[item.name] ? (
              <ChevronUpIcon
                className={`ml-auto h-4 w-4 ${
                  level === 0 && isActive ? 'text-white' : 'text-gray-500'
                }`}
              />
            ) : (
              <ChevronDownIcon
                className={`ml-auto h-4 w-4 ${
                  level === 0 && isActive ? 'text-white' : 'text-gray-500'
                }`}
              />
            )}
          </button>
  
          {expandedMenus[item.name] && (
            <div className="mt-2 ml-4 space-y-1.5">
              {item.submenu.map((subItem) => renderMenuItem(subItem, level + 1))}
            </div>
          )}
        </div>
      );
    }
  
    const path = getRoutePath(String(item.href || ''));
    const isTopLevel = level === 0;
     
    const linkClasses = `
      flex ${isTopLevel ? 'font-medium' : ''} block px-3 py-2 text-sm rounded-lg
      transition-colors duration-200
      ${
        isActive
          ? isTopLevel
            ? 'bg-[#009efb] text-white shadow-md'
            : 'text-[#009efb]'
          : 'text-gray-700 hover:bg-gray-100'
      }
    `;
     
    const iconClasses = `
      mr-3 h-5 w-5
      ${
        isActive
          ? isTopLevel
            ? 'text-white'
            : 'text-[#009efb]'
          : 'text-gray-500 group-hover:text-gray-900'
      }
    `;

    // Simple link item (submenus and deeper levels)
    return (
      <Link
        key={item.name}
        to={path}
        className={linkClasses}
        onClick={() => isMobile && onClose()}
      >
        {item.icon && isTopLevel && (
          <item.icon className={iconClasses} />
        )}
        {item.name}
      </Link>
    );
  };
   
  return (
    <>
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white transform transition-transform duration-200 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:static lg:inset-auto lg:z-30`}
      >
        <div className="flex flex-col h-full">
          {/* User Profile */}
          <div className="flex items-center px-6 py-4 border-b border-gray-200">
            <div className="flex-shrink-0">
              <img
                src={user?.image?.path || '/assets/images/default-avatar.png'}
                alt={user?.name}
                className="h-12 w-12 rounded-full bg-gray-100 object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/assets/images/default-avatar.png';
                }}
              />
            </div>
            <div className="ml-3">
              <p className="text-base font-semibold text-gray-900">{user?.first_name + ' ' + user?.last_name || 'User'}</p>
              <p className="text-sm text-gray-600 capitalize">{user?.role?.name || 'user'}</p>
            </div>
          </div>

          {/* Sidebar navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {filteredMenuItems.map((item) => renderMenuItem(item))}
          </nav>
        </div>
      </div>

      {/* Overlay */}
      {isOpen && isMobile && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
          onClick={onClose}
        />
      )}
    </>
  );
};

export default Sidebar; 